import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { partnerConferencesAPI, purchaseOrdersAPI, taxonomyAPI } from '../services/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { ContentItemLinkDialog } from '../components/content-items/ContentItemLinkDialog';
import { POLineItemSelect } from '../components/purchase-orders/POLineItemSelect';
import { EntityCustomFields } from '../components/shared/EntityCustomFields';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { ArrowLeft, Save, Loader2, Layers, X } from 'lucide-react';
import { toast } from 'sonner';

const REGIONS = ['Asia', 'Americas', 'Europe', 'MEA', 'Other'];
const LANGUAGES = ['English', 'Spanish', 'French', 'Portuguese', 'German', 'Japanese', 'Arabic', 'Other'];
const STATUSES = [
  { value: 'requested', label: 'Requested' },
  { value: 'planning', label: 'Planning' },
  { value: 'scheduled', label: 'Scheduled' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'completed', label: 'Completed' },
  { value: 'cancelled', label: 'Cancelled' },
];

const EMPTY_FORM = {
  title: '',
  city: '',
  country: '',
  region: '',
  training_date: '',
  po_number: '',
  po_line_item_id: '',
  po_line_item_description: '',
  approved_price: '',
  amount_invoiced: '',
  workload: '',
  delivery_language: 'English',
  status: 'requested',
  description: '',
  notes: '',
  content_item_id: '',
  content_item_title: '',
  custom_fields: {},
};

const formatCurrency = (v) => `$${(v || 0).toLocaleString()}`;

export const PartnerConferenceFormPage = () => {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const { user } = useAuth();
  const [form, setForm] = useState(EMPTY_FORM);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [allPos, setAllPos] = useState([]);
  const [contentItemDialogOpen, setContentItemDialogOpen] = useState(false);
  const [categories, setCategories] = useState([]);
  const [customFieldKeys, setCustomFieldKeys] = useState([]);

  useEffect(() => {
    purchaseOrdersAPI.getAll({ active_only: true }).then(setAllPos).catch(() => {});
    taxonomyAPI.getAll().then(setCategories).catch(() => {});
  }, []);

  useEffect(() => {
    if (isEdit) {
      setLoading(true);
      partnerConferencesAPI.getById(id)
        .then(data => {
          setForm({
            title: data.title || '',
            city: data.city || '',
            country: data.country || '',
            region: data.region || '',
            training_date: data.preferred_dates || '',
            po_number: data.po_number || '',
            po_line_item_id: data.po_line_item_id || '',
            po_line_item_description: data.po_line_item_description || '',
            approved_price: data.approved_price ?? '',
            amount_invoiced: data.amount_invoiced ?? '',
            workload: data.workload || '',
            delivery_language: data.delivery_language || 'English',
            status: data.status || 'requested',
            description: data.description || '',
            notes: data.notes || '',
            content_item_id: data.content_item_id || '',
            content_item_title: data.content_item_title || '',
            custom_fields: data.custom_fields || {},
          });
          setCustomFieldKeys(Object.keys(data.custom_fields || {}));
        })
        .catch(() => toast.error('Failed to load conference'))
        .finally(() => setLoading(false));
    }
  }, [id, isEdit]);

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) {
      toast.error('Event title is required');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        approved_price: form.approved_price !== '' ? parseFloat(form.approved_price) : null,
        amount_invoiced: form.amount_invoiced !== '' ? parseFloat(form.amount_invoiced) : null,
      };
      if (isEdit) {
        await partnerConferencesAPI.update(id, payload);
        toast.success('Conference updated successfully');
        navigate(`/partner-conferences/${id}`);
      } else {
        const created = await partnerConferencesAPI.create(payload, user.id);
        toast.success('Conference created successfully');
        navigate(`/partner-conferences/${created.id}`);
      }
    } catch {
      toast.error(isEdit ? 'Failed to update conference' : 'Failed to create conference');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-lime" />
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div>
          <h1 className="text-2xl font-heading font-bold">
            {isEdit ? 'Edit Conference Event' : 'New Conference Event'}
          </h1>
          <p className="text-muted-foreground text-sm">Partner Conferences · AI Tour for Partners</p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Event Info */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Event Details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="title">Event Title <span className="text-destructive">*</span></Label>
              <Input
                id="title"
                placeholder="e.g. AI Tour for Partners – Sydney"
                value={form.title}
                onChange={e => handleChange('title', e.target.value)}
                data-testid="conf-title"
                required
              />
            </div>

            <div className="space-y-1.5">
              <Label>Linked Content Dev Course</Label>
              {form.content_item_id ? (
                <div className="flex items-center justify-between gap-2 border rounded-lg p-2.5 bg-blue-50 border-blue-200" data-testid="conf-selected-content-item">
                  <div className="min-w-0">
                    <p className="text-sm font-medium truncate">{form.content_item_title}</p>
                    <p className="text-xs text-muted-foreground">Content Dev tracker will flag urgency if this course isn't build-complete yet</p>
                  </div>
                  <div className="flex items-center gap-1.5 flex-shrink-0">
                    <Button type="button" variant="outline" size="sm" onClick={() => setContentItemDialogOpen(true)} data-testid="conf-change-content-item-btn">Change</Button>
                    <Button type="button" variant="ghost" size="sm" onClick={() => setForm(f => ({ ...f, content_item_id: '', content_item_title: '' }))} data-testid="conf-clear-content-item-btn">
                      <X className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>
              ) : (
                <Button type="button" variant="outline" className="w-full justify-start" onClick={() => setContentItemDialogOpen(true)} data-testid="conf-open-content-item-search-btn">
                  <Layers className="w-4 h-4 mr-2" />Link to a Content Dev course (optional)...
                </Button>
              )}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="city">City</Label>
                <Input
                  id="city"
                  placeholder="e.g. Sydney"
                  value={form.city}
                  onChange={e => handleChange('city', e.target.value)}
                  data-testid="conf-city"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="country">Country</Label>
                <Input
                  id="country"
                  placeholder="e.g. Australia"
                  value={form.country}
                  onChange={e => handleChange('country', e.target.value)}
                  data-testid="conf-country"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>Region</Label>
                <Select value={form.region} onValueChange={v => handleChange('region', v)}>
                  <SelectTrigger data-testid="conf-region">
                    <SelectValue placeholder="Select region" />
                  </SelectTrigger>
                  <SelectContent>
                    {REGIONS.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="training_date">Training Date</Label>
                <Input
                  id="training_date"
                  type="date"
                  value={form.training_date}
                  onChange={e => handleChange('training_date', e.target.value)}
                  data-testid="conf-date"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="workload">Workload</Label>
                <Input
                  id="workload"
                  placeholder="e.g. Technical tracks"
                  value={form.workload}
                  onChange={e => handleChange('workload', e.target.value)}
                  data-testid="conf-workload"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>Delivery Language</Label>
                <Select value={form.delivery_language} onValueChange={v => handleChange('delivery_language', v)}>
                  <SelectTrigger data-testid="conf-language">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {LANGUAGES.map(l => <SelectItem key={l} value={l}>{l}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Status</Label>
                <Select value={form.status} onValueChange={v => handleChange('status', v)}>
                  <SelectTrigger data-testid="conf-status">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {STATUSES.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Financials */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Financials</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="approved_price">Approved Price (USD)</Label>
                <Input
                  id="approved_price"
                  type="number"
                  min="0"
                  step="0.01"
                  value={form.approved_price}
                  onChange={e => handleChange('approved_price', e.target.value)}
                  placeholder="0.00"
                  data-testid="conf-approved-price"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="amount_invoiced">Amount Invoiced (USD)</Label>
                <Input
                  id="amount_invoiced"
                  type="number"
                  min="0"
                  step="0.01"
                  value={form.amount_invoiced}
                  onChange={e => handleChange('amount_invoiced', e.target.value)}
                  placeholder="0.00"
                  data-testid="conf-amount-invoiced"
                />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="po_number">PO# Invoiced in</Label>
              <Select
                value={form.po_number || 'none'}
                onValueChange={v => setForm(f => ({ ...f, po_number: v === 'none' ? '' : v, po_line_item_id: '', po_line_item_description: '' }))}
              >
                <SelectTrigger data-testid="conf-po">
                  <SelectValue placeholder="Select PO" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Not Assigned</SelectItem>
                  {allPos.map(po => (
                    <SelectItem key={po.id} value={po.po_number}>
                      <div className="flex items-center justify-between w-full">
                        <span>{po.po_number}{po.short_title ? ` — ${po.short_title}` : ''}</span>
                        <Badge variant="outline" className="text-xs ml-2">{formatCurrency(po.available_balance)} available</Badge>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <POLineItemSelect
              pos={allPos}
              poNumber={form.po_number}
              lineItemId={form.po_line_item_id}
              deliveryType="partner_conference"
              deliveryDate={form.training_date}
              invoiceAmount={form.amount_invoiced || form.approved_price}
              onChange={(id, description) => setForm(f => ({ ...f, po_line_item_id: id, po_line_item_description: description }))}
              testIdPrefix="conf-po-line-item"
            />
          </CardContent>
        </Card>

        {/* Description & Notes */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Additional Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                placeholder="Brief event description..."
                value={form.description}
                onChange={e => handleChange('description', e.target.value)}
                rows={3}
                data-testid="conf-description"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="notes">Internal Notes</Label>
              <Textarea
                id="notes"
                placeholder="Internal notes for the team..."
                value={form.notes}
                onChange={e => handleChange('notes', e.target.value)}
                rows={2}
                data-testid="conf-notes"
              />
            </div>
          </CardContent>
        </Card>

        {/* Additional Fields */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Additional Fields</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <EntityCustomFields
              entityType="partner_conference"
              values={form.custom_fields || {}}
              onChange={(v) => setForm(f => ({ ...f, custom_fields: v }))}
              isSuperAdmin={user?.role === 'superadmin'}
              currentUser={user}
              testPrefix="conf-custom-field"
            />
          </CardContent>
        </Card>

        {/* Actions */}
        <div className="flex gap-3 justify-end">
          <Button type="button" variant="outline" onClick={() => navigate(-1)}>
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={saving}
            className="bg-lime text-lime-foreground hover:bg-lime/90"
            data-testid="conf-save-btn"
          >
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            {isEdit ? 'Save Changes' : 'Create Conference'}
          </Button>
        </div>
      </form>
      <ContentItemLinkDialog
        open={contentItemDialogOpen}
        onOpenChange={setContentItemDialogOpen}
        onSelect={(item) => { setContentItemDialogOpen(false); setForm(f => ({ ...f, content_item_id: item?.id || '', content_item_title: item?.title || '' })); }}
      />
    </div>
  );
};

export default PartnerConferenceFormPage;
